<template>
  <label class="custom-checkbox-wrapper" :class="{ disabled }">
    <input
        type="checkbox"
        v-model="localValue"
        class="custom-checkbox-input"
        :disabled="disabled"
        @change="validate('input')"
        @blur="validate('blur')"
    />
    <span class="custom-checkbox-box" :class="{ 'custom-checkbox-box--active': localValue, 'custom-checkbox-box--error': errors?.length }">
      <custom-icon v-if="localValue" :icon="'core-check'" :size="10" :color="'white'"/>
    </span>
    <slot name="label">
      <span v-if="label" class="custom-checkbox-label">{{ label }}</span>
    </slot>
    <Validation
        ref="validator"
        :model-value="!!localValue"
        :name="name"
        :required="required"
        :title="title ?? label"
        :validate-on="validateOn"/>
  </label>
</template>

<script setup lang="ts">

import CustomIcon from "~/components/ui/CustomIcon.vue";

interface Props {
  label?: string
  disabled?: boolean
  name?:  string
  required?: boolean
  title?: string
  validateOn?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  validateOn: () => [],
})
const validator = ref()
const errors = ref([])
const validate = (on: string) => {
  if (!validator.value) return
  nextTick(() => {
    const shouldValidate = on === 'force' || props.validateOn.includes(on)
    const errorsGet = on === 'force' ? validator.value.validate(false) : validator.value.getErrors()
    if (shouldValidate || !errorsGet?.length) {
      errors.value = errorsGet
    }
  })
}
watch(() => validator.value?.isForce, (isForce) => {
  if (isForce) {
    errors.value = validator.value.getErrors()
  }
})


const localValue = defineModel()
</script>

<style scoped lang="scss">
@use 'assets/css/tokens/colors' as *;
@use 'assets/css/tokens/typography' as *;

.custom-checkbox-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  width: fit-content;
}

.custom-checkbox-wrapper.disabled {
  cursor: auto;
  opacity: 0.4;
}

.custom-checkbox-input {
  display: none;
}

.custom-checkbox-box {
  display: flex;
  min-width: 1.375rem;
  min-height: 1.375rem;
  border-radius: 4px;
  background-color: $color-input-bg;
  position: relative;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease;
  &--error{
    border: 1px solid $color-error;
  }
}

.custom-checkbox-box--active {
  background-color: $color-primary;
}

.custom-checkbox-label {
  font-size: $font-size-14;
  line-height: $line-height-22;
  color: $color-dark;
}
</style>
